import React, { useContext, useEffect } from 'react';
import { observer, useLocalStore } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { SyncLoader } from 'react-spinners';
import { Button } from '@patternfly/react-core';

import Form from 'components/JSONSchemaForm';
import { storesContext } from 'stores';
import PatternflyInput from 'components/JSONSchemaForm/PatternflyInput';
import PatternflyTextArea from 'components/JSONSchemaForm/PatternflyTextArea';
import PatternflySelect from 'components/JSONSchemaForm/PatternflySelect';
import ExploreSubscriptions from 'components/ExploreSubscriptions';
import { PendoGuideNames } from 'config/enums';
import SupportStore from './store';
import './styles.scss';
import Section from './Section';

const SupportPane = () => {
  const { t } = useTranslation();
  const rootStore = useContext(storesContext);
  const { eventStore, pendoStore, routerStore, userStore } = rootStore;

  const impactFields = {
    Medium: 3,
    High: 2,
  };

  const schemaForm = {
    type: 'object',
    required: ['title', 'description', 'subcategory', 'impact'],
    properties: {
      title: {
        type: 'string',
        title: t('Issue title'),
      },
      description: {
        type: 'string',
        title: t('Details'),
      },
      courseCode: {
        type: 'string',
        title: t('Impacted offering'),
        enum: [t('Loading catalogue...')],
        enumNames: ['Loading catalogue...'],
      },
      subcategory: {
        type: 'string',
        title: t('Support category'),
        enum: [
          'Account or Enrollment Issues',
          'Learning Platform',
          'Lab Environment',
        ],
        enumNames: [
          t('Account or Enrollment Issues'),
          t('Learning Platform'),
          t('Lab Environment'),
        ],
      },
      impact: {
        type: 'string',
        title: t('Impact level'),
        enum: Object.keys(impactFields),
        enumNames: [t('Medium'), t('High')],
        default: t('Medium'),
      },
      collected: {
        type: 'object',
        title: '',
        properties: {
          username: {
            type: 'string',
            title: t('Username'),
            default: userStore.username,
          },
          email: {
            type: 'string',
            title: t('Email'),
            default: userStore.email,
          },
        },
      },
    },
  };

  const uiSchema = {
    title: {
      'ui:widget': PatternflyInput,
      'ui:options': {
        label: false,
      },
    },
    description: {
      'ui:widget': PatternflyTextArea,
      'ui:options': {
        label: false,
      },
    },
    courseCode: {
      'ui:placeholder': t('Select from catalog...'),
      'ui:options': {
        label: false,
      },
      'ui:widget': PatternflySelect,
    },
    subcategory: {
      'ui:placeholder': t('Select category...'),
      'ui:options': {
        label: false,
      },
      'ui:widget': PatternflySelect,
    },
    impact: {
      'ui:options': {
        label: false,
      },
      'ui:widget': PatternflySelect,
    },
    collected: {
      username: {
        'ui:options': {
          label: false,
        },
        'ui:readonly': true,
        'ui:widget': PatternflyInput,
      },
      email: {
        'ui:options': {
          label: false,
        },
        'ui:readonly': true,
        'ui:widget': PatternflyInput,
      },
    },
  };

  const store = useLocalStore(
    () => new SupportStore(rootStore, schemaForm, uiSchema, impactFields),
  );

  useEffect(() => {
    store.init();
  }, [store]);

  return (
    <React.Fragment>
      <h4 className="pane-title">{t('Support')}</h4>

      <div className="support-component">
        <Section
          bottomDivider
          onClick={() => routerStore.navigate('faq')}
          title={t('Frequently Asked Questions')}
          topDivider
        />

        <Section
          bottomDivider
          description={t(
            'Launch the Resource Center for tutorials, walkthroughs, and other support materials.',
          )}
          onClick={() => {
            if (pendoStore.isPendoInitialized) {
              pendoStore.showGuideByName(PendoGuideNames.RESOURCE_CENTER);
            }
            eventStore.toggleDrawer('support_form');
          }}
          title={t('Resource Center')}
        />

        {userStore.isRHLSTrialSubscriber && userStore.isRedHatLead && (
          <ExploreSubscriptions className="support-component__howtobuy" />
        )}

        {!store.submitted &&
          !store.submitting &&
          !store.error &&
          !userStore.isRHLSTrialSubscriber && (
            <div className="form-container">
              <Section
                description={t(
                  'Fill out this form to create a ticket with our support team. After submitting, you will receive an email with a Service Request Number, and assistance will continue through email.',
                )}
                title={t('Open a support case')}
              >
                <Form
                  buttonProps={{
                    variant: 'primary',
                    className: 'submit-button',
                  }}
                  className="pf-v5-theme-dark"
                  formData={store.formData}
                  onChange={(form) => {
                    store.formData = form.formData;
                  }}
                  onSubmit={() => {
                    store.submit();
                  }}
                  schema={store.schemaForm}
                  uiSchema={store.uiSchema}
                />
              </Section>
            </div>
          )}

        {store.submitted && (
          <div className="success-message-container">
            <h4>
              {t(
                'Your support case has been opened and you will receive an email response as soon as possible.',
              )}
            </h4>

            <Button
              className="success-message-container__button"
              variant="link"
              isInline
              onClick={() => {
                store.formData = {};
                store.submitted = false;
                eventStore.toggleDrawer();
                eventStore.toggleDrawer('support_form');
              }}
            >
              {t('Open another support case')}
            </Button>
          </div>
        )}

        {store.error && (
          <div className="error-message-container">
            <h3>{t('Error submitting support')}</h3>
            <p>{t('Please try again later.')}</p>
          </div>
        )}

        <div className="loading">
          <SyncLoader loading={store.submitting} size={10} />
        </div>
      </div>
    </React.Fragment>
  );
};

export default observer(SupportPane);
